import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import { useAppContext, useUnwrappedTrigger } from '@/hooks'
import { useConfirmationModal } from '@/pages/App/components/ConfirmationService'
import { ConfirmationOptions } from '@/pages/App/types'
import { PostProcessTechnicalColumnDto } from '@/pages/User/pages/Settings/pages/TechnicalColumns/types'
import { useLazyGetTaskExecutionUsingGetQuery } from '@/rtkApi'
import {
	GetTaskExecutionUsingGetApiArg,
	GetTaskExecutionUsingGetApiResponse,
} from '@/rtkApi/mddApiSliceGenerated'
import { logError } from '@/utils/errorHandling'

export const TASK_MONITOR_INTERVAL = 3000
export const TASK_STATE_ERROR = 'ERROR'
export const TASK_STATE_FINISHED = 'FINISHED'
export const TASK_STATE_CANCELLED = 'CANCELLED'

export interface MonitorTaskCallbacks {
	dontCloseOnSuccess?: boolean
	onError?: (error: Error, detail?: string) => void
	onProgressUpdate?: (
		options: ConfirmationOptions,
	) => Promise<ConfirmationOptions | null>
	onSuccess?: (taskId: number) => Promise<void>
	postProcessTechnicalColumnDto?: PostProcessTechnicalColumnDto
}

export const useTaskMonitor = () => {
	const { t } = useAppContext()
	const { openConfirmModal, closeConfirmModal } = useConfirmationModal()
	const [getTaskExecutionTrigger] = useLazyGetTaskExecutionUsingGetQuery()
	const getTaskExecutionProps = useUnwrappedTrigger<
		GetTaskExecutionUsingGetApiResponse,
		GetTaskExecutionUsingGetApiArg
	>(getTaskExecutionTrigger)
	const intervalRef = useRef<NodeJS.Timeout | null>(null)
	const isCancelledRef = useRef<boolean>(false)

	useEffect(() => {
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
			}
		}
	}, [])

	const clearMonitoringInterval = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}
	}, [])

	const stopMonitoring = useCallback(() => {
		clearMonitoringInterval()
		isCancelledRef.current = true
	}, [clearMonitoringInterval])

	const monitorTask = useCallback(
		async (taskId: number, callbacks?: MonitorTaskCallbacks): Promise<void> => {
			if (!taskId) {
				toast(t('TASK_ERROR'), { type: 'error' })
				return
			}

			const {
				onSuccess,
				onError,
				postProcessTechnicalColumnDto,
				onProgressUpdate,
				dontCloseOnSuccess,
			} = callbacks || {}

			isCancelledRef.current = false

			clearMonitoringInterval()

			return new Promise((resolve, reject) => {
				const stopAndResolve = () => {
					stopMonitoring()
					resolve()
				}

				intervalRef.current = setInterval(async () => {
					if (isCancelledRef.current) {
						stopAndResolve()
						return
					}

					try {
						if (isCancelledRef.current) {
							stopAndResolve()
							return
						}

						const progressResponse = await getTaskExecutionProps({ id: taskId })

						if (!progressResponse) {
							stopMonitoring()
							const error = new Error('Invalid response structure')
							logError(error, {
								operation: 'taskMonitor.getProgress',
								details: { taskId },
							})
							onError?.(error)
							reject(error)
							return
						}

						const progress = progressResponse.progress ?? 0
						const state = progressResponse.state

						if (isCancelledRef.current) {
							stopAndResolve()
							return
						}

						if (state === TASK_STATE_ERROR || state === TASK_STATE_CANCELLED) {
							stopMonitoring()
							const errorMessage =
								state === TASK_STATE_CANCELLED
									? t('TASK_CANCELLED')
									: t('TASK_EXECUTION_FAILED')
							const detail = progressResponse.errorMessage
							const error = new Error(errorMessage)
							logError(error, {
								operation: 'taskMonitor.taskFailed',
								details: {
									taskId,
									state,
									errorMessage: progressResponse.errorMessage,
								},
							})
							onError?.(error, detail)
							postProcessTechnicalColumnDto?.(null)
							reject(error)
							return
						}

						if (state === TASK_STATE_FINISHED) {
							stopMonitoring()

							const finalProgressOptions: ConfirmationOptions = {
								title: t('TASK_FINISHED').toUpperCase(),
								description: t('TASK_FINISHED_DESC'),
								isAsync: true,
								preserveProcessingState: true,
								progress: 100,
								taskState: state,
							}

							try {
								if (onProgressUpdate) {
									await onProgressUpdate(finalProgressOptions)
								} else if (dontCloseOnSuccess) {
									await openConfirmModal(finalProgressOptions)
									postProcessTechnicalColumnDto?.(null)
								} else {
									openConfirmModal(finalProgressOptions)
								}

								await onSuccess?.(taskId)

								if (!dontCloseOnSuccess && !onSuccess) {
									closeConfirmModal()
								}

								resolve()
							} catch (error) {
								logError(error, {
									operation: 'taskMonitor.onSuccess',
									details: { taskId },
								})
								onError?.(error as Error)
								reject(error)
							}
							return
						}

						const progressOptions: ConfirmationOptions = {
							title: t('PROCESSING_TASK').toUpperCase(),
							description: t('TASK_RUNNING_WAIT'),
							isAsync: true,
							preserveProcessingState: true,
							progress,
							taskState: state,
						}

						if (!isCancelledRef.current) {
							try {
								if (onProgressUpdate) {
									await onProgressUpdate(progressOptions)
								} else {
									openConfirmModal(progressOptions)
								}
							} catch (error) {
								logError(error, {
									operation: 'taskMonitor.progressUpdate',
									details: { taskId, progress: progressOptions.progress },
								})
								stopAndResolve()
								return
							}
						}
					} catch (error) {
						if (isCancelledRef.current) {
							stopAndResolve()
							return
						}

						const errorDetails: Record<string, unknown> = { taskId }
						if (error && typeof error === 'object') {
							if ('status' in error) {
								errorDetails.httpStatus = error.status
							}
							if ('data' in error) {
								errorDetails.responseData = error.data
							}
						}

						logError(error, {
							operation: 'taskMonitor.polling',
							details: errorDetails,
						})
						onError?.(error as Error)
						stopMonitoring()
						reject(error)
					}
				}, TASK_MONITOR_INTERVAL)
			})
		},
		[
			t,
			openConfirmModal,
			closeConfirmModal,
			getTaskExecutionProps,
			stopMonitoring,
			clearMonitoringInterval,
		],
	)

	return { monitorTask, stopMonitoring }
}
